import { Injectable } from '@angular/core';
import { AdminAppInitializerService } from 'modules/admin/admin-app-initializer.service';
import { BehaviorSubject, Observable, catchError, forkJoin, of, tap } from 'rxjs';
import { AdminHttpProxyService } from './admin-http-proxy.service';
import { Wbe } from 'model/shared/backend-api-response/wbe';
import { ProductConfigResponse } from 'model/shared/backend-api-response/product-config-response';
import { PropertyApiResponse } from 'model/shared/backend-api-response/property-api-response';
import { SpaPropertyInfoApiResponse } from 'model/spa/backend-api-response/spa-property-info-api-response';
import { GolfPropertyApiResponse } from 'model/golf/backend-api-response/golf-property-api-response';
import { GetSeatTokenResponse } from 'model/seat/backend-api-response/get-seat-token-response';
import { ProfileDetailsEntity, ProfileResponse } from 'model/admin/profile.model';
import { Spa } from 'model/spa/backend-api-response/spa';
import { Golf } from 'model/golf/backend-api-response/golf';
import { Seat } from 'model/seat/backend-api-response/seat';
import { Unit } from 'model/unit/backend-api-response/unit';
import { PropertySettings } from 'model/shared/backend-api-response/property-settings';

@Injectable({
  providedIn: 'root',
})
export class AdminProfileService {
  roomPropertyConfigDetails: Wbe;
  spaPropertyConfigDetails: Spa;
  golfPropertyConfigDetails: Golf;
  seatPropertyConfigDetails: Seat;
  unitPropertyConfigDetails: Unit;
  spaPropertyInfo: SpaPropertyInfoApiResponse;
  golfPropertyInfo: GolfPropertyApiResponse;
  propertyApps: string[] = [];
  selectedProfileDetails: ProfileDetailsEntity;
  seatEndPoints = {
    serviceEndPoint: '/wbe-onebookengine-service',
    reservationDetails: 'reservation-details',
    getBookingDetailsWithRateSummary: 'getBookingDetailsWithRateSummary',
  };
  accessToken: string;
  propertySettings: PropertySettings;
  roomPropertyInfo: PropertyApiResponse;
  unitPropertyInfo: PropertyApiResponse;
  showItineraryDetails = new BehaviorSubject<any>('default');
  oneBookProfile;
  dateFunctionConfig;
  reservationDetails;

  constructor(private _conf: AdminAppInitializerService, public httpService: AdminHttpProxyService) {}

  getProfileInfo(filterData): Observable<ProfileResponse> {
    let url = `${this._conf.getCloudDomainName()}/wbe-admin-service/tenants/${this._conf.tenantId}/property/${
      this._conf.propertyId
    }/profile/search?propertyId=${this._conf.propertyId}`;

    if (filterData.firstName) {
      url = url + '&firstName=' + filterData.firstName;
    }
    if (filterData.lastName) {
      url = url + '&lastName=' + filterData.lastName;
    }
    if (filterData.mobileNo) {
      url = url + '&mobileNo=' + filterData.mobileNo;
    }
    if (filterData.emailId) {
      url = url + '&emailId=' + filterData.emailId;
    }
    return this.httpService.get(url, this._conf.serviceHeaders).pipe(tap((data) => console.log(data)));
  }

  getAssetsURL() {
    if (this.roomPropertyConfigDetails?.UrlMap?.ImageUrl && !this.roomPropertyConfigDetails?.UrlMap?.ImageUrl.endsWith('/')) {
      this.roomPropertyConfigDetails.UrlMap.ImageUrl += '/';
    }
    return this.roomPropertyConfigDetails?.UrlMap?.ImageUrl;
  }

  getItineraryDetails(pageNum, pageSize, sortOrder, filterData, startDate?, endDate?) {
    let url =
      this._conf.getCloudDomainName() +
      '/wbe-admin-service/tenants/' +
      this._conf.tenantId +
      '/property/' +
      this._conf.propertyId +
      '/itinerary/search?profileUuid=' +
      filterData.profileUuid +
      '&pageNumber=' +
      pageNum +
      '&pageSize=' +
      pageSize +
      '&sortOrder=' +
      sortOrder +
      '&ignoreTimeStamp=true';
    if (startDate) {
      url = url + '&startDateTime=' + startDate + 'T00:00:00';
    }
    if (endDate) {
      url = url + '&endDateTime=' + endDate + 'T23:59:00';
    }

    return this.httpService.get(url, this._conf.serviceHeaders);
  }

  getUpandPastItineraryDetails(pageNum, pageSize, index, endDate, filterData) {
    let url;
    if (index === 0) {
      url = `${this._conf.getCloudDomainName()}/wbe-admin-service/tenants/${this._conf.tenantId}/property/${
        this._conf.propertyId
      }/getFutureItineraries?endDateTime=${endDate}T00:00:00&profileUuid=${
        filterData.profileUuid
      }&pageNumber=${pageNum}&pageSize=${pageSize}&ignoreTimeStamp=true`;
    } else if (index === 1) {
      url = `${this._conf.getCloudDomainName()}/wbe-admin-service/tenants/${this._conf.tenantId}/property/${
        this._conf.propertyId
      }/getPastItineraries?endDateTime=${endDate}T00:00:00&profileUuid=${
        filterData.profileUuid
      }&pageNumber=${pageNum}&pageSize=${pageSize}&ignoreTimeStamp=true`;
    }
    return this.httpService.get(url, this._conf.serviceHeaders);
  }

  getAllPropertyInfo(): Observable<ProductConfigResponse> {
    let url = `${this._conf.getCloudDomainName()}/wbe-property-service/property/tenants/${this._conf.tenantId}/propertyId/${
      this._conf.propertyId
    }`;
    return this.httpService.get(url, this._conf.serviceHeaders).pipe(tap((data) => console.log(data)));
  }

  getAccessTokenIntervel() {
    setInterval(() => {
      this.getSeatToken();
    }, 10 * 60 * 1000);
  }

  async getSeatToken() {
    let url = `${this._conf.getCloudDomainName()}${this.seatEndPoints.serviceEndPoint}/seat/tenants/${this._conf.tenantId}/propertyId/${
      this._conf.propertyId
    }/token?appName=seat`;
    await this.httpService
      .get(url, this._conf.serviceHeaders)
      .toPromise()
      .then((res: GetSeatTokenResponse) => {
        if (res && res.success) {
          if (!this.accessToken) {
            this.getAccessTokenIntervel();
          }
          this.accessToken = res.seatToken.access_token;
        }
      });
  }

  getHeaders() {
    return {
      headers: {
        'X-Token': sessionStorage.getItem('token'),
        SEAT_JWT_TOKEN: `Bearer ${this.accessToken}`,
      },
    };
  }

  getResFork(variable) {
    let observe = [];

    let room_url =
      this._conf.getCloudDomainName() +
      '/' +
      'wbe-reservation-service/reservation' +
      '/tenants/' +
      this._conf.tenantId +
      ('/context/' + this.roomPropertyConfigDetails.ContextId + '/') +
      `propertyName/${this.roomPropertyConfigDetails?.PropNameKeyList?.reservation}`;
    let unit_url =
      this._conf.getCloudDomainName() +
      '/' +
      'wbe-reservation-service/reservation' +
      '/tenants/' +
      this._conf?.tenantId +
      ('/context/' + this.unitPropertyConfigDetails?.ContextId + '/') +
      `propertyName/${this.unitPropertyConfigDetails?.PropNameKeyList?.reservation}`;
    if (variable && variable.length > 0) {
      variable.forEach((ele) => {
        if (ele.activity == 'room') {
          observe.push(
            this.httpService
              .get(
                room_url + `/arriveDate/${ele.startDateTime}/guestName/${ele.lastName}?appName=wbe&confirmation=${ele.confirmationId}`,
                this._conf.serviceHeaders
              )
              .pipe(catchError(this.handleError('', [])))
          );
        } else if (ele.activity == 'spa') {
          observe.push(
            this.httpService
              .get(
                `${this._conf.getCloudDomainName()}/wbe-spa-service/spa/tenants/${this._conf.tenantId}/propertyId/${
                  this._conf.propertyId
                }/confirmationId/${ele.confirmationId}/getSpaReservation`,
                this._conf.serviceHeaders
              )
              .pipe(catchError(this.handleError('', [])))
          );
        } else if (ele.activity == 'golf') {
          observe.push(
            this.httpService
              .get(
                `${this._conf.getCloudDomainName()}/wbe-golf-service/golf/tenants/${this._conf.tenantId}/propertyId/${
                  this._conf.propertyId
                }/confirmationId/${ele.confirmationId}/getGolfReservation`,
                this._conf.serviceHeaders
              )
              .pipe(catchError(this.handleError('', [])))
          );
        } else if (ele.activity == 'unit') {
          observe.push(
            this.httpService
              .get(
                unit_url + `/arriveDate/${ele.startDateTime}/guestName/${ele.lastName}?appName=unit&confirmation=${ele.confirmationId}`,
                this._conf.serviceHeaders
              )
              .pipe(catchError(this.handleError('', [])))
          );
        } else if (ele.activity == 'activity' || ele.activity == 'reserve' || ele.activity == 'seat') {
          let langCode = sessionStorage.getItem(`${this._conf.tenantId}-${this._conf.propertyId}-selectedLanguage`);
          let seaturl = `${this._conf.getCloudDomainName()}${this.seatEndPoints.serviceEndPoint}/activity/tenants/${
            this._conf.tenantId
          }/property/${this._conf.propertyId}/${this.seatEndPoints.getBookingDetailsWithRateSummary}?confirmationCode=${
            ele.confirmationId
          }&appName=seat&languageId=${langCode}&propertyType=${ele.activity == 'activity' ? 'General' : 'Restaurant'}`;
          observe.push(this.httpService.get(seaturl, this.getHeaders()).pipe(catchError(this.handleError('', []))));
        }
      });
      return forkJoin(observe);
    }
  }

  formateDate = function (odate) {
    let date = new Date(odate);
    var mm = date.getMonth() + 1; // getMonth() is zero-based
    var dd = date.getDate();
    return [date.getFullYear(), (mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd].join('-');
  };

  getSpaPropertyDetails(): Observable<SpaPropertyInfoApiResponse> {
    let url =
      this._conf.getCloudDomainName() +
      '/wbe-spa-service/spa/tenants/' +
      this._conf.tenantId +
      '/propertyId/' +
      this._conf.propertyId +
      '/propertyInfo' +
      '?appName=spa';
    return this.httpService.get(url, this._conf.serviceHeaders).pipe(tap((data) => console.log(data)));
  }

  getRoomPropertyDetails(): Observable<PropertyApiResponse> {
    let url =
      this._conf.getCloudDomainName() +
      '/' +
      'wbe-property-service/property' +
      '/tenants/' +
      this._conf.tenantId +
      '/context/' +
      this.roomPropertyConfigDetails?.ContextId +
      '/' +
      'propertyName/' +
      this.roomPropertyConfigDetails?.PropNameKeyList?.config +
      '/propertyId/' +
      this._conf.propertyId +
      '?appName=wbe&ts=' +
      new Date().getTime();
    return this.httpService.get(url, this._conf.serviceHeaders).pipe(tap((data) => console.log(data)));
  }

  getUnitPropertyDetails(): Observable<PropertyApiResponse> {
    let url =
      this._conf.getCloudDomainName() +
      '/' +
      'wbe-property-service/property' +
      '/tenants/' +
      this._conf.tenantId +
      '/context/' +
      this.unitPropertyConfigDetails?.ContextId +
      '/' +
      'propertyName/' +
      this.unitPropertyConfigDetails?.PropNameKeyList?.config +
      '/propertyId/' +
      this._conf.propertyId +
      '?appName=unit&ts=' +
      new Date().getTime();
    return this.httpService.get(url, this._conf.serviceHeaders).pipe(tap((data) => console.log(data)));
  }

  getGolfPropertyDetails(): Observable<GolfPropertyApiResponse> {
    let url =
      this._conf.getCloudDomainName() +
      '/wbe-golf-service/golf/tenants/' +
      this._conf.tenantId +
      '/propertyId/' +
      this._conf.propertyId +
      '/propertyInfo' +
      '?appName=golf';
    return this.httpService.get(url, this._conf.serviceHeaders).pipe(tap((data) => console.log(data)));
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(error['error'] as T);
    };
  }
}
